import React, { useEffect, Suspense } from "react";
import { SignInButton } from "@clerk/clerk-react";
import SignUpButton from "../components/SignUpButton";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  useMediaQuery,
  Link,
  Button,
  useTheme,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import LogoHeader from "../components/LogoHeader";
import Footer from "../components/Footer";
import ManualInstallModal from "../components/ManualInstallModal";
import { useAuthStatus, AuthStatus } from "../hooks/useAuthStatus";

import { holidayBanner } from "../holidays/holidayBannerConfig";

const GetAppIcon = React.lazy(() => import("@mui/icons-material/GetApp"));
const CloseIcon = React.lazy(() => import("@mui/icons-material/Close"));

/* --- Constants & Animations --- */

const BG_OVERLAY =
  "linear-gradient(to bottom, " +
  "rgba(0, 0, 0, 0.25) 0%, " +
  "rgba(0, 0, 0, 0.25) 50%, " +
  "rgba(0, 0, 0, 0.1) 100%)"; // WCAG-compliant contrast overlay
const APP_FONT = "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif";

// Subtle kenburns for mobile
const kenburnsMobile = keyframes`
  0% { transform: scale(1); }
  100% { transform: scale(1.04); }
`;

// More cinematic, slower kenburns for desktop
const kenburnsDesktop = keyframes`
  0% { transform: scale(1) translateY(0); }
  50% { transform: scale(1.10) translateY(-1%); }
  100% { transform: scale(1.14) translateY(0); }
`;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); }`;

const fadeInCentered = keyframes`
  from { opacity: 0; } 
  to { opacity: 1; }
`;

/* --- Styled Components --- */
const mobileButtonBase = {
  height: "42px",
  width: "100%",
  maxWidth: "280px",
  borderRadius: "9999px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "17px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  padding: "0 20px",
  boxSizing: "border-box",
};

const StyledMobileButton = styled(Button)({
  ...mobileButtonBase,
  backgroundColor: "#FFFFFF",
  color: "#000000",
  boxShadow: "none",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#F5F5F5",
    boxShadow: "none",
  },
});

const CategoryLink = styled(Link)({
  color: "#1d1d1f",
  textDecoration: "none",
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.2em",
  opacity: 0.7,
  transition: "all 0.3s ease",
  "&:hover": { opacity: 1 },
});

const PrimaryButton = styled(Button)({
  ...mobileButtonBase,
  backgroundColor: "#FFFFFF",
  color: "#000000",
  fontSize: "18px",
  padding: "0 40px",
  ["@media (min-width:900px)"]: {
    fontSize: "20px",
    height: "46px",
    padding: "0 44px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
  },
  "&:hover": { backgroundColor: "#F5F5F5" },
});

// Holiday Banner visibility check (config-based)
const isHolidayBannerActive = holidayBanner && holidayBanner.active;

const LandingPage = ({ handleInstallClick, showInstallButton }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { status: authStatus, isLoading } = useAuthStatus();
  const [showManualModal, setShowManualModal] = React.useState(false);
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  const showHolidayBanner = isHolidayBannerActive && !bannerDismissed;

  const handleDismissBanner = () => {
    setBannerDismissed(true);
  };

  const HolidayBanner = showHolidayBanner ? (
    <Box
      sx={{
        position: "absolute",
        top: { xs: 12, md: 8 },
        left: "50%",
        width: { xs: "calc(100% - 24px)", md: "auto" },
        maxWidth: { xs: "none", md: "480px" },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        px: { xs: 2.5, md: 3 },
        py: { xs: 1.5, md: 2 },
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        borderRadius: { xs: "14px", md: "16px" },
        boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
        border: "1px solid rgba(215, 38, 96, 0.2)",
        zIndex: 100,
        transform: "translateX(-50%)",
        animation: `${fadeInCentered} 0.6s ease-out`,
      }}
    >
      {/* Close button */}
      <Box
        onClick={handleDismissBanner}
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          width: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          bgcolor: "rgba(0,0,0,0.08)",
          cursor: "pointer",
          transition: "background 0.2s",
          "&:hover": { bgcolor: "rgba(0,0,0,0.15)" },
        }}
      >
        <Suspense fallback={null}>
          <CloseIcon sx={{ fontSize: 14, color: "#666" }} />
        </Suspense>
      </Box>
      {/* Mobile: horizontal row with image + text */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: { xs: "100%", md: "auto" },
        }}
      >
        {holidayBanner.image && (
          <Box
            component="img"
            src={holidayBanner.image}
            alt={holidayBanner.title}
            sx={{
              width: { xs: 32, md: 48 },
              height: { xs: 32, md: 48 },
              borderRadius: "10px",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "13px", md: "15px" },
              color: "#d72660",
              lineHeight: 1.2,
            }}
          >
            {holidayBanner.title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "11px", md: "12px" },
              color: "#666",
              lineHeight: 1.3,
              mt: 0.25,
              whiteSpace: "pre-line",
            }}
          >
            {holidayBanner.subtitle}
          </Typography>
        </Box>
      </Box>
      {/* CTA button - full width on mobile, inline on desktop */}
      <SignUpButton mode="modal">
        <Button
          variant="contained"
          sx={{
            bgcolor: "#d72660",
            color: "#fff",
            fontWeight: 700,
            borderRadius: "9999px",
            width: { xs: "100%", md: "auto" },
            px: { xs: 3, md: 3.5 }, // More horizontal padding
            py: { xs: 1.1, md: 1.2 }, // More vertical padding
            fontSize: { xs: "13px", md: "13px" },
            minHeight: { xs: 36, md: 40 },
            textTransform: "none",
            whiteSpace: "nowrap",
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { bgcolor: "#c02055", boxShadow: "none" },
          }}
        >
          {holidayBanner.cta}
        </Button>
      </SignUpButton>
    </Box>
  ) : null;

  useEffect(() => {
    // Redirect authenticated users to home
    // Users needing registration will be handled by App.js
    if (authStatus === AuthStatus.AUTHENTICATED) {
      navigate("/home", { replace: true });
    }
  }, [authStatus, navigate]);

  // Show loading state while determining auth, or redirect if authenticated
  if (isLoading || authStatus === AuthStatus.AUTHENTICATED) {
    return <Box sx={{ minHeight: "100svh", bgcolor: "#fff" }} />;
  }

  const handleInstallWithFallback = () => {
    // Try the install click handler, if it fails, show manual instructions
    try {
      handleInstallClick();
    } catch (error) {
      setShowManualModal(true);
    }
  };

  /* --- Optimized Background Component --- */
  const BackgroundStyles = (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <Box
        component="picture"
        sx={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      >
        <picture style={{ width: "100%", height: "100%", display: "block" }}>
          {/* 4K/Large Desktop - (min-width: 1921px) */}
          <source
            media="(min-width: 1921px)"
            srcSet="/hero-4k-img.webp"
            type="image/webp"
          />

          {/* Standard Desktop - (900px to 1920px) */}
          <source
            media="(min-width: 900px)"
            srcSet="/hero-desktop-img.webp"
            type="image/webp"
          />

          {/* Mobile - (Default and up to 899px) */}
          <source
            media="(max-width: 899px)"
            srcSet="/hero-mobile-img.webp"
            type="image/webp"
          />

          {/* Fallback */}
          <Box
            component="img"
            src="/hero-desktop-img.webp"
            alt="The Linkup background"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: {
                xs: "50% 35%", // Mobile
                md: "center 100%", // Desktop
              },
              animation: {
                xs: `${kenburnsMobile} 30s infinite alternate ease-in-out`, // Subtle on mobile
                md: `${kenburnsDesktop} 50s infinite alternate ease-in-out`, // Slower on desktop
              },
              willChange: "transform",
            }}
          />
        </picture>
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: BG_OVERLAY,
          display: { xs: "none", md: "block" },
        }}
      />
    </Box>
  );

  if (isSmall) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          overflowY: "auto",
          pt: "env(safe-area-inset-top, 0px)",
          pb: "calc(2rem + env(safe-area-inset-bottom, 0px))",
        }}
        className="page-scroll-container"
      >
        <Box
          sx={{
            height: "64px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "20px",
            zIndex: 10,
            shrink: 0,
          }}
        >
          <LogoHeader forcedColorMode="dark" isMobile={true} />
          <SignInButton mode="modal">
            <Button
              variant="outlined"
              sx={{
                color: "#1d1d1f",
                borderColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "24px",
                px: 3,
                py: 0.6,
                fontSize: "13px",
                fontWeight: 700,
                textTransform: "none",
                backdropFilter: "blur(4px)",
                "&:hover": {
                  borderColor: "#1d1d1f",
                  bgcolor: "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              Log In
            </Button>
          </SignInButton>
        </Box>

        <Box
          sx={{
            minHeight: "calc(100svh - 76px)",
            position: "relative",
            m: "0 16px 12px",
            borderRadius: "24px",
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            "& img": {
              objectPosition: "center 40% !important",
              objectFit: "cover !important",
            },
          }}
        >
          {HolidayBanner}
          {BackgroundStyles}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(18,18,18,0.92) 0%, rgba(18,18,18,0.72) 40%, rgba(18,18,18,0.32) 80%, rgba(0,0,0,0.08) 100%)",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              zIndex: 2,
              position: "relative",
              px: 3,
              pt: showHolidayBanner ? "38%" : "30%",
              pb: 4,
            }}
          >
            <Box sx={{ textAlign: "center", mb: "auto" }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: "34px",
                  fontWeight: 400,
                  color: "#fff",
                  mb: 1.5,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  textShadow: "0 2px 8px rgba(0,0,0,0.45)",
                }}
              >
                Spontaneous plans
                <br />
                <Box component="span" sx={{ fontWeight: 700 }}>
                  made simple.
                </Box>
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "17px",
                  fontWeight: 600,
                  fontStyle: "italic",
                  maxWidth: "260px",
                  mx: "auto",
                  opacity: 0.88,
                  letterSpacing: "0.02em",
                }}
              >
                Don't overthink it. Just link up.
              </Typography>
            </Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <SignUpButton mode="modal">
                <StyledMobileButton
                  sx={{
                    bgcolor: "#fff",
                    color: "#000",
                    width: "100%",
                    height: "52px",
                    fontSize: "16px",
                    fontWeight: 800,
                    borderRadius: "26px",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                  }}
                >
                  Create Account
                </StyledMobileButton>
              </SignUpButton>
              {showInstallButton && (
                <Button
                  onClick={handleInstallWithFallback}
                  sx={{
                    ...mobileButtonBase,
                    width: "100%",
                    height: "52px",
                    bgcolor: "rgba(255,255,255,0.10)",
                    color: "#fff",
                    borderRadius: "26px",
                    border: "2px solid rgba(255,255,255,0.65)",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.18)",
                    backdropFilter: "blur(10px)",
                    fontSize: "16px",
                    fontWeight: 700,
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    // gap: 1.5,
                    pl: 2.5,
                    pr: 2.5,
                  }}
                >
                  <Suspense fallback={null}>
                    <GetAppIcon sx={{ fontSize: 20, ml: -0.5 }} />
                  </Suspense>
                  Download App
                </Button>
              )}
              <Button
                onClick={() => navigate("/how-it-works")}
                sx={{
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "15px",
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                  textDecorationThickness: "1.5px",
                  bgcolor: "transparent",
                  px: 1,
                  py: 0.5,
                  mb: 1,
                  letterSpacing: "0.01em",
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    bgcolor: "transparent",
                    opacity: 0.8,
                  },
                }}
              >
                How it works
              </Button>
            </Box>
          </Box>
        </Box>
        <Footer isSmall={false} showOwnership={false} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        position: "relative",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          height: "70px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 6,
          pt: 1,
          bgcolor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          zIndex: 1001,
          flexShrink: 0,
          position: "sticky",
          top: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "48px",
            display: "flex",
            gap: 4,
            alignItems: "center",
          }}
        >
          <CategoryLink href="/about">About</CategoryLink>
          <CategoryLink href="/contact">Contact</CategoryLink>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LogoHeader forcedColorMode="light" />
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: "48px",
            display: "flex",
            gap: 3,
            alignItems: "center",
          }}
        >
          <SignInButton mode="modal">
            <Typography
              sx={{
                color: "#1d1d1f",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                opacity: 0.8,
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                "&:hover": { opacity: 1 },
              }}
            >
              Log In
            </Typography>
          </SignInButton>
          <Button
            onClick={handleInstallWithFallback}
            startIcon={
              <Suspense fallback={null}>
                <GetAppIcon sx={{ fontSize: 18 }} />
              </Suspense>
            }
            sx={{
              color: "#1d1d1f",
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              opacity: 0.85,
              px: 1.5,
              py: 0.5,
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.12)",
              "&:hover": {
                opacity: 1,
                bgcolor: "rgba(0,0,0,0.04)",
                borderColor: "rgba(0,0,0,0.2)",
              },
            }}
          >
            Install App
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          position: "relative",
          p: "0 20px 20px 20px",
          minHeight: "600px",
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: "relative",
            borderRadius: "24px",
            overflow: "hidden",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {HolidayBanner}
          {BackgroundStyles}
          {/* Social icons - positioned bottom right within hero */}
          {/* <Box
            sx={{
              position: "absolute",
              bottom: 24,
              right: 24,
              display: "flex",
              flexDirection: "row",
              gap: 1.5,
              zIndex: 20,
            }}
          >
            <a
              href="https://www.instagram.com/thelinkup.ca/"
              target="_blank"
              rel="noopener"
              aria-label="Follow us on Instagram"
              tabIndex={0}
            >
              <SocialSideButton>
                <Suspense fallback={null}>
                  <InstagramIcon sx={{ fontSize: 18 }} />
                </Suspense>
              </SocialSideButton>
            </a>
            <a
              href="https://twitter.com/TheLinkupInc/"
              target="_blank"
              rel="noopener"
              aria-label="Follow us on X"
              tabIndex={0}
            >
              <SocialSideButton>
                <Suspense fallback={null}>
                  <XIcon sx={{ fontSize: 18 }} />
                </Suspense>
              </SocialSideButton>
            </a>
            <a
              href="https://www.facebook.com/people/The-Linkup/61565677733492/"
              target="_blank"
              rel="noopener"
              aria-label="Follow us on Facebook"
              tabIndex={0}
            >
              <SocialSideButton>
                <Suspense fallback={null}>
                  <FacebookIcon sx={{ fontSize: 18 }} />
                </Suspense>
              </SocialSideButton>
            </a>
          </Box> */}
          {/* Now Live Chip - bottom right corner of hero with subtle shadow */}
          {/* <Chip
            label="NOW LIVE IN MONTREAL"
            sx={{
              position: "absolute",
              bottom: 24,
              right: 24,
              zIndex: 10,
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#222",
              fontWeight: 600,
              fontSize: "10px",
              letterSpacing: "0.7px",
              textTransform: "uppercase",
              px: 2,
              py: 0.5,
              height: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: 2,
              alignSelf: "flex-end",
            }}
          /> */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 5,
              textAlign: "center",
              position: "relative",
            }}
          >
            <Box
              sx={{
                mb: 6,
                animation: `${fadeIn} 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  color: "#FFFFFF",
                  fontFamily: APP_FONT,
                  fontSize: "clamp(2.5rem, 6vw, 5rem)",
                  fontWeight: 500,
                  lineHeight: 1.08,
                  letterSpacing: "-0.02em",
                  mb: 0,
                  textShadow: "0px 2px 4px rgba(0, 0, 0, 0.35)",
                }}
              >
                Spontaneous plans
                <br />
                <Box component="span" sx={{ fontWeight: 700 }}>
                  made simple.
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 3,
                  color: "#fff",
                  fontSize: "24px",
                  fontWeight: 500,
                  fontStyle: "italic",
                  letterSpacing: "0.01em",
                  fontFamily: APP_FONT,
                  animation: `${fadeIn} 1.4s ease-out forwards`,
                  textAlign: "center",
                  opacity: 0.95,
                }}
              >
                Don't overthink it. Just link up.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <SignUpButton mode="modal">
                <PrimaryButton>Get Started</PrimaryButton>
              </SignUpButton>
              <Button
                component="a"
                href="/how-it-works"
                onClick={() => navigate("/how-it-works")}
                sx={{
                  color: "#fff",
                  textTransform: "none",
                  fontSize: "20px",
                  fontWeight: 700,
                  textDecoration: "none",
                  bgcolor: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "9999px",
                  height: "46px",
                  minWidth: "220px",
                  px: "44px",
                  border: "1.5px solid rgba(255,255,255,0.35)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.25)",
                    borderColor: "rgba(255,255,255,0.5)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
                  },
                }}
                aria-label="Learn how The Linkup works"
              >
                How it works
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          pb: 4,
          pt: 3,
          width: "100%",
          textAlign: "center",
          zIndex: 5,
          shrink: 0,
          paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {/* <Typography
          sx={{
            color: "rgba(34,34,34,0.7)", // much darker for visibility
            fontSize: "12px",
            fontFamily: APP_FONT,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontWeight: 700,
            textShadow: "0px 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          © 2026 The Linkup. All Rights Reserved.
        </Typography> */}
        <Footer isSmall={false} showOwnership={false} />
      </Box>

      {/* Manual Install Instructions Modal */}
      <ManualInstallModal
        open={showManualModal}
        onClose={() => setShowManualModal(false)}
      />
    </Box>
  );
};

export default LandingPage;
